/**
 * PB-/BK-Detail · Medien & Dokumente · DocuSign-Verträge
 *
 * Liste der versendeten DocuSign-Dokumente des Profils (Status, Empfänger) und
 * Drawer „Vertrag aus DocuSign senden": Vorlage, Rolle und Empfänger wählen,
 * die Datenfelder füllt das Backend aus dem Profil. Unterschriebene PDFs
 * landen per Connect-Webhook automatisch in den Dokumenten (Kategorie Vertrag).
 *
 * API: GET /v2/admin/docusign/envelopes?user_id=…, GET /v2/admin/docusign/send/*,
 * POST /v2/admin/users/{id}/docusign/envelopes (users.write).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, EditDrawer } from '../../../../components/ui';
import { getThemeColors } from '../../../../lib/theme';
import {
  docusignErrorKind,
  docusignSendError,
  v2Docusign,
  type DocusignEnvelopeRow,
  type DocusignErrorKind,
  type DocusignRecipientType,
  type DocusignTemplate,
  type DocusignTemplateRole,
} from '../../../../lib/v1';
import type { BadgeTone } from '../../../../types';
import { formatMediaDate } from './shared';

interface Props {
  userId: number;
  canWrite: boolean;
  dark: boolean;
  /** Nach erfolgreichem Versand (z. B. Dokumentenliste neu laden). */
  onSent?: () => void;
}

/** Rolle mit fester Besetzung aus der Backend-Konfiguration (z. B. Geschäftsführer). */
type TemplateRole = DocusignTemplateRole & { fixed_recipient?: string | null };

/** Felder ohne eigenen Namen („Text 9a1d…", „atb.docusignFields…") füllt das CRM nie. */
const UNNAMED_FIELD = /^(text [0-9a-f-]{36}|atb\.docusignfields\.)/i;

const namedFields = (fields: string[]) => Array.from(new Set(fields.filter(f => !UNNAMED_FIELD.test(f))));

const STATUS_TONE: Record<string, BadgeTone> = {
  sent: 'info',
  delivered: 'info',
  completed: 'success',
  declined: 'error',
  voided: 'neutral',
};

export function DocusignPanel({ userId, canWrite, dark, onSent }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [envelopes, setEnvelopes] = useState<DocusignEnvelopeRow[]>([]);
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setEnvelopes(await v2Docusign.envelopes(userId));
    } catch {
      setEnvelopes([]);
    }
  }, [userId]);

  useEffect(() => { void load(); }, [load]);

  const sent = (row: DocusignEnvelopeRow) => {
    setOpen(false);
    setNotice(tr('pflege.media.docusign.sent_success', { email: row.recipient_email ?? '' }));
    setEnvelopes(prev => [row, ...prev.filter(e => e.id !== row.id)]);
    onSent?.();
  };

  return (
    <Card dark={dark} style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        padding: '14px 20px', borderBottom: `1px solid ${t.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: t.heading }}>{tr('pflege.media.docusign.title')}</h3>
        {canWrite && (
          <Button size="sm" dark={dark} onClick={() => { setNotice(null); setOpen(true); }}>
            {tr('pflege.media.docusign.send_button')}
          </Button>
        )}
      </div>

      <div style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {notice && (
          <div role="status" style={{ fontSize: 12.5, color: '#067647' }}>{notice}</div>
        )}
        {envelopes.length === 0 ? (
          <div style={{ fontSize: 12.5, color: t.muted, fontStyle: 'italic', padding: '4px 0' }}>
            {tr('pflege.media.docusign.empty')}
          </div>
        ) : envelopes.map(e => (
          <div key={e.id} style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            padding: '8px 0', borderBottom: `1px solid ${t.borderSoft}`,
          }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>{e.template_name ?? e.envelope_id}</div>
              <div style={{ fontSize: 12, color: t.muted }}>
                {tr('pflege.media.docusign.row_meta', {
                  recipient: e.recipient_name ?? e.recipient_email ?? '',
                  date: formatMediaDate(e.sent_at),
                })}
              </div>
            </div>
            <Badge tone={STATUS_TONE[e.status] ?? 'neutral'} dark={dark}>
              {tr(`pflege.media.docusign.status.${e.status}`, { defaultValue: e.status })}
            </Badge>
          </div>
        ))}
      </div>

      {open && (
        <SendDrawer userId={userId} dark={dark} onClose={() => setOpen(false)} onSent={sent} />
      )}
    </Card>
  );
}

function SendDrawer({ userId, dark, onClose, onSent }: {
  userId: number;
  dark: boolean;
  onClose: () => void;
  onSent: (row: DocusignEnvelopeRow) => void;
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [templates, setTemplates] = useState<DocusignTemplate[]>([]);
  const [recipientTypes, setRecipientTypes] = useState<DocusignRecipientType[]>([]);
  const [loadError, setLoadError] = useState<DocusignErrorKind | null>(null);
  const [loading, setLoading] = useState(true);
  const [templateId, setTemplateId] = useState('');
  const [role, setRole] = useState('');
  const [recipient, setRecipient] = useState('self');
  const [internalRecipient, setInternalRecipient] = useState('assigned_admin');
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([v2Docusign.sendTemplates(), v2Docusign.sendRecipientTypes()])
      .then(([tpls, types]) => {
        if (!alive) return;
        setTemplates(tpls);
        setRecipientTypes(types);
      })
      .catch(e => { if (alive) setLoadError(docusignErrorKind(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const template = templates.find(tpl => tpl.template_id === templateId) ?? null;
  const selectedRole = template?.roles.find(r => r.role === role) ?? null;
  const otherRoles: TemplateRole[] = (template?.roles ?? []).filter(r => r.role !== role);
  const fixedRoles = otherRoles.filter(r => r.fixed_recipient);
  const choosableRoles = otherRoles.filter(r => !r.fixed_recipient);
  const selectedFields = selectedRole ? namedFields(selectedRole.fields) : [];

  const pickTemplate = (id: string) => {
    setTemplateId(id);
    const tpl = templates.find(x => x.template_id === id);
    // Erste Rolle (Routing-Reihenfolge 1) ist in der Regel die Kundin/der Kunde
    setRole(tpl?.roles[0]?.role ?? '');
  };

  const submit = async () => {
    if (!template || !role) return;
    setBusy(true);
    setSendError(null);
    try {
      const row = await v2Docusign.send(userId, {
        template_id: template.template_id,
        template_name: template.name,
        role,
        recipient,
        internal_recipient: choosableRoles.length > 0 ? internalRecipient : null,
      });
      onSent(row);
    } catch (e) {
      setSendError(docusignSendError(e) ?? tr(`settings.docusign.error.${docusignErrorKind(e)}`));
    } finally {
      setBusy(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', border: `1px solid ${t.border}`, borderRadius: 8,
    padding: '8px 10px', fontSize: 13, color: t.heading,
    background: t.inputBg, fontFamily: 'inherit', outline: 'none',
  };
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: t.text, display: 'block', marginBottom: 4 };
  const optionLabel = (r: DocusignRecipientType) =>
    r.group === 'familienkonto' ? `${tr('settings.docusign.contact_type')}: ${r.label}` : r.label;

  return (
    <EditDrawer open onClose={onClose} title={tr('pflege.media.docusign.drawer_title')} dark={dark}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {loading && <div style={{ fontSize: 13, color: t.muted }}>{tr('settings.docusign.loading')}</div>}
        {loadError && (
          <div role="alert" style={{ fontSize: 13, color: '#b42318' }}>{tr(`settings.docusign.error.${loadError}`)}</div>
        )}
        {!loading && !loadError && (
          <>
            <div>
              <label htmlFor="ds-send-template" style={labelStyle}>{tr('pflege.media.docusign.template')}</label>
              <select id="ds-send-template" value={templateId} onChange={e => pickTemplate(e.target.value)} style={inputStyle}>
                <option value="">{tr('pflege.media.docusign.template_placeholder')}</option>
                {templates.map(tpl => (
                  <option key={tpl.template_id} value={tpl.template_id}>{tpl.name}</option>
                ))}
              </select>
              {templates.length === 0 && (
                <div style={{ fontSize: 12, color: t.muted, marginTop: 4 }}>{tr('settings.docusign.empty')}</div>
              )}
            </div>

            {template && (
              <>
                <div>
                  <label htmlFor="ds-send-role" style={labelStyle}>{tr('pflege.media.docusign.role')}</label>
                  <select id="ds-send-role" value={role} onChange={e => setRole(e.target.value)} style={inputStyle}>
                    {template.roles.map(r => <option key={r.role} value={r.role}>{r.role}</option>)}
                  </select>
                </div>

                <div>
                  <label htmlFor="ds-send-recipient" style={labelStyle}>{tr('pflege.media.docusign.recipient')}</label>
                  <select id="ds-send-recipient" value={recipient} onChange={e => setRecipient(e.target.value)} style={inputStyle}>
                    {recipientTypes.filter(r => r.value !== 'assigned_admin').map(r => (
                      <option key={r.value} value={r.value}>{r.value === 'self' ? tr('pflege.media.docusign.recipient_self') : optionLabel(r)}</option>
                    ))}
                  </select>
                </div>

                {fixedRoles.map(r => (
                  <div key={r.role} style={{ fontSize: 12.5, color: t.text }}>
                    {tr('pflege.media.docusign.fixed_recipient', { role: r.role, name: r.fixed_recipient })}
                  </div>
                ))}

                {choosableRoles.length > 0 && (
                  <div>
                    <label htmlFor="ds-send-internal" style={labelStyle}>
                      {tr('pflege.media.docusign.internal_recipient', { roles: choosableRoles.map(r => r.role).join(', ') })}
                    </label>
                    <select id="ds-send-internal" value={internalRecipient} onChange={e => setInternalRecipient(e.target.value)} style={inputStyle}>
                      {recipientTypes.map(r => (
                        <option key={r.value} value={r.value}>{r.value === 'self' ? tr('pflege.media.docusign.recipient_self') : optionLabel(r)}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={{ fontSize: 12, color: t.muted }}>
                  {selectedFields.length > 0
                    ? tr('pflege.media.docusign.fields_hint', { fields: selectedFields.join(', ') })
                    : tr('pflege.media.docusign.no_fields_hint')}
                </div>
              </>
            )}

            {sendError && <div role="alert" style={{ fontSize: 13, color: '#b42318' }}>{sendError}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" dark={dark} onClick={onClose}>{tr('common.action.cancel')}</Button>
              <Button dark={dark} disabled={!template || !role || busy} onClick={() => { void submit(); }}>
                {busy ? tr('pflege.media.docusign.sending') : tr('pflege.media.docusign.send')}
              </Button>
            </div>
          </>
        )}
      </div>
    </EditDrawer>
  );
}
